// Roadmap data — two independent views.
//
//   PIPELINE       → PUBLIC roadmap on the homepage. A CURATED subset: only the
//                    user-facing items worth advertising. No infra/ops, no
//                    "obvious" basics, and NO paid-tier-only features (those
//                    will live on a future Free vs Paid comparison page).
//
//   PIPELINE_FULL  → INTERNAL Progress page (admin-only, /admin/progress).
//                    The COMPLETE record — everything, including shipped basics,
//                    ops/plumbing, paid-tier features and ideas under
//                    consideration. Grouped into categories for readability.
//
// The two lists are intentionally decoupled: editing the public roadmap must
// never drop items from the internal Progress record.

// ══════════════════════════════════════════════════════════════════════════
//  PUBLIC ROADMAP PHASES (curated)
// ══════════════════════════════════════════════════════════════════════════

const PUB_PORTFOLIO = {
  phase: 'Portfolio Module',
  status: 'done',
  items: [
    { label: 'Holdings table with live prices', done: true },
    { label: 'Portfolio dashboard with performance chart', done: true },
    { label: 'Transaction management (buy, sell, FX, fees…)', done: true },
    { label: 'Full transaction history with filters & sorting', done: true },
    { label: 'Interactive price charts (1D–All)', done: true },
    { label: 'Multi-currency support with FX conversion', done: true },
    { label: 'Portfolio allocation chart (by holding, currency, asset type, dividends)', done: true },
    { label: 'Dividend tracking (history, TTM income, yield, forward forecast)', done: true },
  ],
};

const PUB_STOCKS_CRYPTO = {
  phase: 'Stocks & Crypto Workspace',
  status: 'done',
  items: [
    { label: 'S&P 500 heatmap (treemap by market cap, daily change)', done: true },
    { label: 'Crypto heatmap with CoinGecko top 200', done: true },
    { label: 'Stocks & Crypto overview (movers, popular, ticker tape)', done: true },
    { label: 'Sector and category filters', done: true },
    { label: 'Market Fear & Greed sentiment index', done: true },
  ],
};

const PUB_IN_DEV = {
  phase: 'In Development',
  status: 'active',
  items: [
    { label: 'Settings page (theme, currency, language)', done: false },
    { label: 'Asset logos for crypto and ETFs in portfolio', done: false },
    { label: 'Commodities workspace (gold, oil, metals)', done: false },
    { label: 'Watchlist (star instruments, dedicated section)', done: false },
    { label: 'Advanced portfolio analytics (Sharpe, drawdown, benchmarks)', done: false },
    { label: 'Chart and heatmap improvements', done: false },
    { label: 'Mobile & tablet layout polish (responsive design)', done: false },
    { label: 'Overall performance optimisation', done: false },
    { label: 'Feedback / "Report a bug" widget', done: false },
  ],
};

const PUB_PLANNED = {
  phase: 'Planned',
  status: 'planned',
  items: [
    { label: 'Multi-index heatmaps (Nasdaq 100, Dow Jones, Russell)', done: false },
    { label: 'Time-period change toggle (1D / 1W / 1M / 3M / 6M / YTD / 1Y)', done: false },
    { label: 'Full instrument financials (ratios, margins, balance sheet, cash flow)', done: false },
    { label: 'Tax reporting tools (per-country exports)', done: false },
  ],
};

// ══════════════════════════════════════════════════════════════════════════
//  INTERNAL PROGRESS PHASES (complete — admin only)
// ══════════════════════════════════════════════════════════════════════════

const FULL_PORTFOLIO = {
  phase: 'Portfolio Module',
  status: 'done',
  items: [
    { label: 'Holdings table with live prices', done: true },
    { label: 'Portfolio dashboard with performance chart', done: true },
    { label: 'Transaction management (buy, sell, FX, fees…)', done: true },
    { label: 'Full transaction history with filters & sorting', done: true },
    { label: 'Interactive price charts (1D–All)', done: true },
    { label: 'Multi-currency support with FX conversion', done: true },
    { label: 'Per-trade P/L (unrealized gain/loss per purchase)', done: true },
    { label: 'Portfolio allocation chart (by holding, currency, asset type, dividends)', done: true },
    { label: 'Dividend tracking (history, TTM income, yield, forward forecast)', done: true },
    { label: 'XIRR — true annualised return across all cash flows', done: true },
    { label: 'Email registration + password reset', done: true },
  ],
};

const FULL_STOCKS_CRYPTO = {
  phase: 'Stocks & Crypto Workspace',
  status: 'done',
  items: [
    { label: 'S&P 500 heatmap (treemap by market cap, daily change)', done: true },
    { label: 'Crypto heatmap with CoinGecko top 200', done: true },
    { label: 'Stocks & Crypto overview (movers, popular, ticker tape)', done: true },
    { label: 'Global instrument search in navbar', done: true },
    { label: 'Instrument detail pages with full charts and key stats', done: true },
    { label: 'Sector and category filters', done: true },
    { label: 'Market Fear & Greed sentiment index', done: true },
    { label: 'Public access for guest users (Stocks & Crypto without login)', done: true },
  ],
};

const FULL_IN_DEV = {
  phase: 'In Development',
  status: 'active',
  items: [
    ...PUB_IN_DEV.items,
    { label: 'Public UUIDs for users + portfolios (variant B)', done: false },
    { label: 'Detailed admin statistics (growth, retention, activity)', done: false },
    { label: 'Admin: delete user accounts', done: false },
    { label: 'HTML email templates with branding', done: false },
    { label: 'Yahoo Finance cache layer (429 protection)', done: false },
  ],
};

const FULL_ADMIN_OPS = {
  phase: 'Admin & Ops',
  status: 'done',
  items: [
    { label: 'Admin panel — users, tiers, landing preview, progress', done: true },
    { label: 'Daily Postgres backup to Cloudflare R2 (GFS retention)', done: true },
    { label: 'Sentry error monitoring (frontend + backend)', done: true },
    { label: 'Better Stack uptime monitoring', done: true },
    { label: 'Rate limiting on auth endpoints', done: true },
    { label: 'GDPR data export endpoint', done: true },
    { label: 'Legal stack: Privacy, Terms, Cookies, Subprocessors', done: true },
  ],
};

// Paid-tier / monetisation work — internal only, never on the public roadmap.
const FULL_MONETISATION = {
  phase: 'Monetisation & Paid Tier',
  status: 'pipeline',
  items: [
    { label: 'Free vs Paid tier feature gating', done: false },
    { label: 'Free vs Paid comparison page', done: false },
    { label: 'Subscription billing integration (Stripe)', done: false },
    { label: 'Per-trade P/L gated behind paid tier', done: false },
  ],
};

const FULL_PLANNED = {
  phase: 'Planned',
  status: 'planned',
  items: [
    { label: 'Multi-index heatmaps (Nasdaq 100, Dow Jones, Russell)', done: false },
    { label: 'Time-period change toggle (1D / 1W / 1M / 3M / 6M / YTD / 1Y)', done: false },
    { label: 'Full instrument financials (ratios, margins, balance sheet, cash flow)', done: false },
    { label: 'Tax reporting tools (per-country exports)', done: false },
  ],
};

const FULL_PIPELINE = {
  phase: 'Pipeline',
  status: 'pipeline',
  items: [
    { label: 'Email & push notifications for price alerts', done: false },
    { label: 'Two-factor authentication (2FA)', done: false },
    { label: 'CSV import from common brokers (IBKR, Trading 212, Lynx…)', done: false },
    { label: 'ETF composition heatmap (holdings overlap analysis)', done: false },
    { label: 'International exchange heatmaps (FTSE, DAX, Nikkei, ASX)', done: false },
    { label: 'Shared / family portfolios', done: false },
    { label: 'AI-powered investment insights', done: false },
  ],
};

// ── Public roadmap (homepage) — curated subset ───────────────────────────
export const PIPELINE = [
  PUB_PORTFOLIO,
  PUB_STOCKS_CRYPTO,
  PUB_IN_DEV,
  PUB_PLANNED,
];

// ── Internal Progress page (admin-only) — complete record ────────────────
export const PIPELINE_FULL = [
  FULL_PORTFOLIO,
  FULL_STOCKS_CRYPTO,
  FULL_ADMIN_OPS,
  FULL_IN_DEV,
  FULL_MONETISATION,
  FULL_PIPELINE,
  FULL_PLANNED,
];

export const STATUS_CONFIG = {
  done:     { dot: '#4ade80', bar: '#4ade80', label: 'Completed',     labelColor: '#4ade80', labelBg: 'rgba(74,222,128,0.12)',  labelBorder: 'rgba(74,222,128,0.28)' },
  active:   { dot: '#818cf8', bar: '#818cf8', label: 'In Progress',   labelColor: '#a5b4fc', labelBg: 'rgba(129,140,248,0.14)', labelBorder: 'rgba(129,140,248,0.30)' },
  pipeline: { dot: '#f59e0b', bar: '#f59e0b', label: 'Pipeline',      labelColor: '#fbbf24', labelBg: 'rgba(245,158,11,0.14)',  labelBorder: 'rgba(245,158,11,0.30)' },
  planned:  { dot: '#334155', bar: '#1e293b', label: 'Planned',       labelColor: '#64748b', labelBg: 'rgba(255,255,255,0.05)', labelBorder: 'rgba(255,255,255,0.08)' },
};
